/**
 * MONSTER TOWN — THE ZOO. DATA ONLY. Owned by the ZOO crew.
 *
 * VENUE #3, AND IT IS A DATA DROP. Nothing in `src/venue/**` knows a zoo exists:
 * this file is a menu of animals, a set of placeables generated from that menu,
 * and one `defineVenue`. The room, its shell, its starter kit, its camera
 * framing and its visitors all come from the venue system unchanged.
 *
 * ===========================================================================
 *  THE LEARNING CONTENT — this is the best of it in the whole game
 * ===========================================================================
 * Every animal declares THREE things, and each one is a different question a
 * three-year-old can answer by looking:
 *
 *   food      what it eats        -> the feeding game at the trough (matching)
 *   habitat   where it lives      -> put it beside the right pen (sorting)
 *   sound     what it says        -> tap it and hear (listening)
 *
 * None of the three can be got WRONG in a way that costs anything. A wrong food
 * is a giggle and a swap; an animal beside the wrong pen is simply an animal
 * beside a pen, and the right one sparkles. There is no score and no timer.
 *
 * A NEW SPECIES IS A ROW IN `ANIMALS`. The placeable, its 3D art, its book page,
 * its shop entry and its feeding card are all derived from that row.
 *
 * `zoo_feeder` and `venue_zoo` are named by the reference errand in
 * `src/content/errands.ts`, which is owned by somebody else. Those two ids are
 * load-bearing and must not be renamed.
 */

import { FOOT_1x1, FOOT_2x1, FOOT_3x2, type Cell } from '../core/grid'
import { defineCollectible, type CollectionEntry } from '../core/collection'
import { definePlaceable } from '../core/placeable'
import type { PaletteName } from '../core/palette'
import type { PlaceableId, SceneId, VenueId } from '../core/types'
import { defineVenue, type VenueDef } from '../venue/types'
import { venueExit } from './venue'
import { animalArt, feederArt, keeperArt, penArt, zooBenchArt, zooGateArt, type AnimalShape } from '../venues/zoo/art'

const id = (s: string): PlaceableId => s as PlaceableId
const cell = (gx: number, gz: number): Cell => ({ gx, gy: 0, gz })

export const ZOO_SCENE = 'venue_zoo' as SceneId
export const ZOO_VENUE = 'zoo' as VenueId

// ---------------------------------------------------------------------------
// habitats
// ---------------------------------------------------------------------------

export type HabitatId = 'grass' | 'water' | 'rock' | 'tree'

export interface HabitatDef {
  readonly id: HabitatId
  readonly devName: string
  readonly defId: PlaceableId
  /** Icon key for the picture card. Comes from the HUD's shared icon set. */
  readonly icon: string
  readonly cost: number
}

export const HABITATS: readonly HabitatDef[] = [
  { id: 'grass', devName: 'Grass pen', defId: id('zoo_pen_grass'), icon: 'plant', cost: 0 },
  { id: 'water', devName: 'Water pen', defId: id('zoo_pen_water'), icon: 'water', cost: 0 },
  { id: 'tree', devName: 'Tree pen', defId: id('zoo_pen_tree'), icon: 'tree', cost: 0 },
  { id: 'rock', devName: 'Rock pen', defId: id('zoo_pen_rock'), icon: 'block', cost: 0 },
]

export function habitat(h: HabitatId): HabitatDef {
  return HABITATS.find((x) => x.id === h) as HabitatDef
}

// ---------------------------------------------------------------------------
// the animals
// ---------------------------------------------------------------------------

export interface AnimalDef {
  /** Collection-book id. */
  readonly id: string
  /** DEV ONLY. Never shown to a kid (LAW #4). */
  readonly devName: string
  /** The placeable a kid puts in the zoo. */
  readonly defId: PlaceableId
  readonly shape: AnimalShape
  readonly body: PaletteName
  readonly belly: PaletteName
  readonly trim: PaletteName
  /** WHERE IT LIVES. Matched against the pen it is standing beside. */
  readonly habitat: HabitatId
  /** WHAT IT EATS. An item id — the same bag the kitchen fills. */
  readonly food: string
  /** WHAT IT SAYS. An audio cue id. */
  readonly sound: string
  /** Coins for a correct feed. Always a gain. */
  readonly coins: number
  readonly cost: number
  readonly tier?: string
}

export const ANIMALS: readonly AnimalDef[] = [
  {
    id: 'animal_hoppy',
    devName: 'Long-eared hopper',
    defId: id('zoo_animal_hoppy'),
    shape: 'hop',
    body: 'monsterCream',
    belly: 'monsterBelly',
    trim: 'wallPink',
    habitat: 'grass',
    food: 'ing_veg',
    sound: 'toy_boing',
    coins: 6,
    cost: 0,
  },
  {
    id: 'animal_splash',
    devName: 'Paddle-bill',
    defId: id('zoo_animal_splash'),
    shape: 'waddle',
    body: 'monsterSky',
    belly: 'flowerWhite',
    trim: 'roofMustard',
    habitat: 'water',
    food: 'ing_berry',
    sound: 'water_plop',
    coins: 6,
    cost: 0,
  },
  {
    id: 'animal_munch',
    devName: 'Big honey-lover',
    defId: id('zoo_animal_munch'),
    shape: 'big',
    body: 'wood',
    belly: 'woodLight',
    trim: 'woodDark',
    habitat: 'tree',
    food: 'ing_honey',
    sound: 'munch',
    coins: 8,
    cost: 0,
  },
  {
    id: 'animal_tiny',
    devName: 'Rock nibbler',
    defId: id('zoo_animal_tiny'),
    shape: 'tiny',
    body: 'monsterLilac',
    belly: 'monsterBelly',
    trim: 'wallLilac',
    habitat: 'rock',
    food: 'ing_milk',
    sound: 'nibble',
    coins: 6,
    cost: 0,
  },
  {
    id: 'animal_stripe',
    devName: 'Tall stripe',
    defId: id('zoo_animal_stripe'),
    shape: 'tall',
    body: 'monsterSunny',
    belly: 'monsterBelly',
    trim: 'woodDark',
    habitat: 'grass',
    food: 'ing_grain',
    sound: 'nibble',
    coins: 10,
    cost: 70,
  },
  {
    id: 'animal_climb',
    devName: 'Curly-tail climber',
    defId: id('zoo_animal_climb'),
    shape: 'climb',
    body: 'monsterCoral',
    belly: 'monsterBelly',
    trim: 'roofRed',
    habitat: 'tree',
    food: 'ing_fruit',
    sound: 'giggle',
    coins: 10,
    cost: 90,
    tier: 'tier1',
  },
]

export function animalByDef(defId: string): AnimalDef | undefined {
  return ANIMALS.find((a) => a.defId === defId)
}
export function animal(id_: string): AnimalDef | undefined {
  return ANIMALS.find((a) => a.id === id_)
}

/**
 * A PAGE PER ANIMAL, derived from the row above so a new species cannot forget
 * to bring one. `places: ['zoo']` is the place tag the zoo stamps on itself;
 * core never interprets it.
 */
export const ANIMAL_COLLECTIBLES: readonly CollectionEntry[] = ANIMALS.map((a) =>
  defineCollectible({
    id: a.id,
    kind: 'animal',
    devName: a.devName,
    icon: 'monster',
    where: [{ places: ['zoo'] }],
    coins: a.coins,
    likes: { food: [a.food], habitat: [a.habitat], sound: a.sound },
  }),
)

// ---------------------------------------------------------------------------
// placeables, generated from the data above
// ---------------------------------------------------------------------------

/**
 * ONE FACTORY, EVERY ANIMAL. LAW #1 is satisfied identically for all of them:
 * the body bounces, hearts come off it, and it makes its own noise — the sound
 * is the point, so a kid taps every animal in the zoo just to hear them.
 */
function animalPlaceable(a: AnimalDef) {
  return definePlaceable<{ pats: number }>({
    id: a.defId,
    devName: a.devName,
    category: 'toy',
    scale: 'interior',
    footprint: FOOT_1x1,
    cost: a.cost,
    ...(a.tier === undefined ? {} : { tier: a.tier }),
    stackable: false,
    walkable: false,
    tints: [a.body, 'monsterTeal', 'monsterBubblegum', 'monsterLime'],
    tags: ['zoo', 'animal', a.habitat, a.cost === 0 ? 'starter' : 'zooExtra'],
    initialState: () => ({ pats: 0 }),
    buildArt: (ctx) =>
      animalArt(ctx, {
        shape: a.shape,
        body: ctx.tint ?? a.body,
        belly: a.belly,
        trim: a.trim,
      }),
    interact: (ctx) => ({
      feedback: [
        { kind: 'anim', clip: 'bounce', seconds: 0.7, target: 'body' },
        { kind: 'anim', clip: 'wobble', seconds: 0.6, target: 'head' },
        { kind: 'particles', effect: 'hearts', count: 6 },
        { kind: 'spawn', what: `zoo:pat:${a.id}` },
        { kind: 'sound', cue: a.sound },
        { kind: 'state', patch: { pats: ctx.state.pats + 1 } },
      ],
      cooldownMs: 320,
    }),
  })
}

/**
 * ============ A PEN IS THREE TILES WIDE, AND THAT IS LOAD-BEARING ============
 *
 * It was two, and a 2-wide pen's centre falls on a tile BOUNDARY — so the 1x1
 * cell an animal stands in is always half a tile off the pen's axis, and half a
 * tile is less than half an animal. All four starter animals were drawn across
 * their own fence. Three tiles gives the yard a MIDDLE column, the animal stands
 * on the pen's axis, and the rail clears its silhouette by about a whole tile.
 * See the long note at `penArt` in `src/venues/zoo/art.ts`, and
 * `npm run zoo-check`, which measures it.
 *
 * The art derives everything from this footprint, so this is still one number in
 * a data file. The only other thing that had to move is the starter layout below
 * — and the room went from 14 tiles wide to 15 so four pens and their gaps land
 * evenly in it.
 */
function penPlaceable(h: HabitatDef) {
  return definePlaceable<{ tidied: number }>({
    id: h.defId,
    devName: h.devName,
    category: 'venueFixture',
    scale: 'interior',
    footprint: FOOT_3x2,
    cost: h.cost,
    stackable: false,
    walkable: false,
    tags: ['zoo', 'pen', h.id, 'starter'],
    initialState: () => ({ tidied: 0 }),
    buildArt: (ctx) => penArt(ctx, h.id),
    interact: (ctx) => ({
      feedback: [
        { kind: 'anim', clip: 'jiggle', seconds: 0.5, target: 'inside' },
        { kind: 'particles', effect: h.id === 'water' ? 'bubbles' : 'leaves', count: 6 },
        { kind: 'sound', cue: h.id === 'water' ? 'water_splash' : 'leaves_rustle' },
        { kind: 'state', patch: { tidied: ctx.state.tidied + 1 } },
      ],
      cooldownMs: 260,
    }),
  })
}

export const ANIMAL_PLACEABLES = ANIMALS.map(animalPlaceable)
export const PEN_PLACEABLES = HABITATS.map(penPlaceable)

/**
 * THE TROUGH. The end of the reference errand and the way into the feeding
 * game, in one object. `ui` opens the picture panel; the `spawn` verb before it
 * is how `src/venues/zoo/index.ts` hears the tap and takes a carried dish.
 */
export const zooFeeder = definePlaceable<{ feeds: number }>({
  id: id('zoo_feeder'),
  devName: 'Feeding trough',
  category: 'venueFixture',
  scale: 'interior',
  footprint: FOOT_2x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tags: ['zoo', 'starter', 'venue:bell'],
  initialState: () => ({ feeds: 0 }),
  buildArt: (ctx) => feederArt(ctx),
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'jiggle', seconds: 0.5, target: 'food' },
      { kind: 'particles', effect: 'sparkle', count: 6 },
      { kind: 'spawn', what: 'zoo:feeder' },
      { kind: 'ui', panel: 'zoofeed' },
      { kind: 'sound', cue: 'plate_clink' },
      { kind: 'state', patch: { feeds: ctx.state.feeds + 1 } },
    ],
    cooldownMs: 420,
  }),
})

export const zooBench = definePlaceable<{ sat: number }>({
  id: id('zoo_bench'),
  devName: 'Zoo bench',
  category: 'furniture',
  scale: 'interior',
  footprint: FOOT_2x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['woodLight', 'wallMint', 'wallPink'],
  tags: ['zoo', 'seat', 'starter'],
  initialState: () => ({ sat: 0 }),
  buildArt: (ctx) => zooBenchArt(ctx),
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'wobble', seconds: 0.45, target: 'seat' },
      { kind: 'monster', action: 'sit', seconds: 1.6 },
      { kind: 'particles', effect: 'sparkle', count: 4 },
      { kind: 'sound', cue: 'bench_creak' },
      { kind: 'state', patch: { sat: ctx.state.sat + 1 } },
    ],
    cooldownMs: 300,
  }),
})

/** The way in, out in the town. A door, exactly like the other venue fronts. */
export const zooGate = definePlaceable<{ visits: number }>({
  id: id('zoo_gate'),
  devName: 'Zoo gate',
  category: 'townProp',
  scale: 'exterior',
  footprint: FOOT_3x2,
  cost: 0,
  stackable: false,
  walkable: false,
  tags: ['zoo', 'door', 'starter'],
  initialState: () => ({ visits: 0 }),
  buildArt: (ctx) => zooGateArt(ctx),
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'swing', seconds: 0.5, target: 'door' },
      { kind: 'particles', effect: 'sparkle', count: 8 },
      { kind: 'sound', cue: 'door_open' },
      { kind: 'state', patch: { visits: ctx.state.visits + 1 } },
      { kind: 'scene', to: ZOO_SCENE },
    ],
    cooldownMs: 600,
  }),
})

/**
 * THE ZOOKEEPER, standing outside the gate.
 *
 * The reference errand's giver. Tapping him is the `talk` step, and it is also
 * how the errand ARRIVES — `src/venues/zoo/index.ts` answers the `zoo:keeper`
 * verb by offering it if the clock says it is feeding time. He is a placeable
 * rather than a wandering neighbour on purpose: the NPC crew owns roaming
 * monsters, and this crew must be able to prove the errand end to end without
 * waiting on theirs.
 */
export const zooKeeper = definePlaceable<{ chats: number }>({
  id: id('zoo_keeper'),
  devName: 'Zookeeper',
  category: 'townProp',
  scale: 'exterior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tags: ['zoo', 'npc', 'starter'],
  initialState: () => ({ chats: 0 }),
  buildArt: (ctx) => keeperArt(ctx),
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'bounce', seconds: 0.6, target: 'body' },
      { kind: 'anim', clip: 'wobble', seconds: 0.5, target: 'bowl' },
      { kind: 'monster', action: 'greet', seconds: 1.2 },
      { kind: 'particles', effect: 'notes', count: 6 },
      { kind: 'spawn', what: 'zoo:keeper' },
      { kind: 'sound', cue: 'npc_hello' },
      { kind: 'state', patch: { chats: ctx.state.chats + 1 } },
    ],
    cooldownMs: 500,
  }),
})

// ---------------------------------------------------------------------------
// THE VENUE
// ---------------------------------------------------------------------------

/**
 * `items: []` IS DELIBERATE AND IS NOT A GAP.
 *
 * The venue service loop builds an order out of a base and a scoop; with no
 * menu it builds none, and its own comment says what happens then: "the
 * customer just sits and enjoys the room, which is still not a failure". That is
 * exactly right for a zoo — visitors walk in, sit on a bench and watch the
 * animals, and they can never be kept waiting for something a kid forgot to
 * serve. The zoo's own loop (feeding) lives at the trough, where it belongs.
 */
export const zoo: VenueDef = defineVenue({
  id: ZOO_VENUE,
  kind: 'zoo',
  sceneId: ZOO_SCENE,
  devName: 'Zoo',
  // Wider than the shops: four pens across the back wall is the picture that
  // says "zoo" the moment a kid walks in, and it needs the width.
  // 15 wide so four 3-tile pens and the four gaps between them land evenly along
  // the back wall (see `penPlaceable`). Widening a grid is save-safe: no cell a
  // kid has ever placed something in stops existing.
  grid: { id: 'venue_zoo', scale: 'interior', width: 15, depth: 10, levels: 4, originX: -19.5, originZ: 40 },
  shell: {
    // Outdoors-in-a-room: sky-blue walls, a sandy path floor, teal trim. Nothing
    // else in the game looks like this, which is the whole point — a venue that
    // is a colour swap of another venue does not read as somewhere new.
    wall: 'wallBlue',
    wallTrim: 'roofTeal',
    floor: 'sand',
    floorAlt: 'path',
    roofBeam: 'woodDark',
    floorStyle: 'diamond',
    rug: null,
  },
  doorCell: cell(7, 9),
  items: [],
  seatDefIds: [zooBench.id],
  maxScoops: 1,
  starter: [
    { defId: venueExit.id, anchor: cell(7, 9) },
    // four habitats along the back wall, one of each, evenly spaced with a tile
    // of walkway between them
    { defId: id('zoo_pen_grass'), anchor: cell(0, 1) },
    { defId: id('zoo_pen_water'), anchor: cell(4, 1) },
    { defId: id('zoo_pen_tree'), anchor: cell(8, 1) },
    { defId: id('zoo_pen_rock'), anchor: cell(12, 1) },
    // one animal in the MIDDLE COLUMN of each pen's yard, ALREADY CORRECTLY
    // SORTED — the finished picture is what teaches the rule, and a kid then
    // moves them about. The middle column is what keeps an animal inside its own
    // rail; see `penPlaceable` above.
    { defId: id('zoo_animal_hoppy'), anchor: cell(1, 3) },
    { defId: id('zoo_animal_splash'), anchor: cell(5, 3) },
    { defId: id('zoo_animal_munch'), anchor: cell(9, 3) },
    { defId: id('zoo_animal_tiny'), anchor: cell(13, 3) },
    // the trough, right where you walk in
    { defId: zooFeeder.id, anchor: cell(5, 6) },
    { defId: zooBench.id, anchor: cell(2, 7) },
    { defId: zooBench.id, anchor: cell(10, 7) },
  ],
})

/** Where the gate and the keeper stand on a brand new town. */
export const ZOO_TOWN_SEED = {
  /** South-west front, clear of the ice cream shop and clear of the pond. */
  gate: cell(2, 22),
  keeper: cell(4, 21),
} as const

// ---------------------------------------------------------------------------
// registration exports — `src/content/index.ts` spreads these
// ---------------------------------------------------------------------------

export const ZOO_CONTENT = [
  ...PEN_PLACEABLES,
  ...ANIMAL_PLACEABLES,
  zooFeeder,
  zooBench,
  zooGate,
  zooKeeper,
]

/** A kid can lay out and run the whole zoo with no coins at all. */
export const ZOO_FREE_IDS: PlaceableId[] = [
  ...HABITATS.map((h) => h.defId),
  ...ANIMALS.filter((a) => a.cost === 0).map((a) => a.defId),
  zooFeeder.id,
  zooBench.id,
  zooGate.id,
  zooKeeper.id,
]

export const ZOO_VENUE_DEFS: VenueDef[] = [zoo]
